import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  DatePicker,
  Grid,
  Segmented,
  Select,
  Skeleton,
  Tooltip,
  theme,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { ApiService } from "../../api";
import type { AnalyticsDashboard, AnalyticsTemplate } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { PageHeader, MetaDot } from "../../components/PageHeader";
import { Panel, Divider } from "../../components/surfaces";
import { space } from "../../theme/space";
import { MAX_PAGE_WIDTH } from "../Reports/ui";
import { useRefreshOnTabFocus } from "../../tabs/tabContext";
import ActivityChart, { ACTIVITY_COLORS } from "./ActivityChart";
import {
  BarRow,
  Chip,
  EmptyState,
  PanelHeading,
  SectionLabel,
  SeriesKey,
  Trend,
  formatAgo,
  formatDays,
  formatDuration,
} from "./parts";
import ProcessSpeed from "./ProcessSpeed";

/**
 * Analytics.
 *
 * The page answers three questions in the order a person actually asks them —
 * **how much is happening**, **how is it progressing**, **where are the
 * problems** — and each band exists because it answers one of them. Nothing is
 * here because the data was available.
 *
 * What it replaced is worth recording, because the failure modes are common
 * ones. The old page showed four figures of which **two were hardcoded** (an
 * "Expired requests" 7 and an "Active Users" 1 typed into the JSX), a 3-D pie
 * chart per template — a dozen of them on a real deployment, each too small to
 * read and none comparable with the next — and a table of the last ten requests
 * regardless of anything the reader had filtered. There was no date range at
 * all, so every number meant "since the beginning of time" without saying so,
 * and the four endpoints behind it were each unscoped: any logged-in user read
 * counts over every request in the database.
 *
 * The rules this one is built to:
 *
 * * **One window, stated on the page.** All four figures and the chart come
 *   from one call over one range, and the header says which range. A dashboard
 *   whose parts silently measure different periods is worse than no dashboard.
 * * **A number that cannot be computed is not shown as zero.** "No completed
 *   cases yet" and "an average of zero" are different facts; the average
 *   reports its own sample size.
 * * **Nothing is decoration.** Every figure is either a headline answer or a
 *   route to the list that explains it — the waiting states and the process
 *   rows navigate to exactly the filtered list the number came from.
 * * **Plain language, throughout.** "Started", "Completed", "Where work is
 *   waiting". The engine's own vocabulary (status kind, stage key, attempt)
 *   never reaches the screen.
 */

/**
 * The page fills the width it is given, up to the app's ceiling.
 *
 * It used to stop at 1440. The reasoning was sound about one band and wrong
 * about the page: four figures spread across 1,900px do drift apart — but
 * capping the *whole page* to fix that left a monitor's worth of dead grey
 * beside the chart, which reads as a layout that failed rather than a measure
 * that was chosen. So the cap moved to the thing that actually suffers from
 * stretching (`KPI_BAND_MAX`), and everything with a horizontal axis — the
 * activity chart, the per-state bars, the two lists — gets the room it can use.
 */
const MAX_WIDTH = MAX_PAGE_WIDTH;

/**
 * The four headline figures stay a group.
 *
 * They are one thought separated by hairlines; distributed across the full
 * width of a large monitor, each number sits alone in the middle of half a
 * metre of empty panel and the row stops reading as a row.
 */
const KPI_BAND_MAX = 1180;

type Preset = "7d" | "30d" | "90d" | "12m" | "custom";

const ISO = "YYYY-MM-DD";

/** Presets, not a date picker alone: "last 30 days" is the question people ask. */
const rangeFor = (
  preset: Preset,
  custom: [Dayjs, Dayjs] | null
): { from: string; to: string } => {
  const today = dayjs();
  if (preset === "custom" && custom) {
    return { from: custom[0].format(ISO), to: custom[1].format(ISO) };
  }
  const spans: Record<string, Dayjs> = {
    "7d": today.subtract(6, "day"),
    "30d": today.subtract(29, "day"),
    "90d": today.subtract(89, "day"),
    "12m": today.subtract(12, "month").add(1, "day"),
  };
  return {
    from: (spans[preset] ?? spans["30d"]).format(ISO),
    to: today.format(ISO),
  };
};

const readableRange = (from: string, to: string) => {
  const start = dayjs(from);
  const end = dayjs(to);
  const sameYear = start.year() === end.year();
  return `${start.format(sameYear ? "D MMM" : "D MMM YYYY")} – ${end.format(
    "D MMM YYYY"
  )}`;
};

/**
 * One headline figure.
 *
 * A plain block, not a card: these four are one thought, and four bordered
 * boxes would draw three edges that mean nothing. The separators between them
 * are hairlines, added only where the band does not wrap.
 */
const Kpi = ({
  label,
  value,
  unit,
  footer,
  tooltip,
  divider,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  footer?: React.ReactNode;
  tooltip?: string;
  divider: boolean;
}) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        minWidth: 0,
        paddingLeft: divider ? space.wide : 0,
        borderLeft: divider ? `1px solid ${token.colorSplit}` : undefined,
      }}
    >
      <Tooltip title={tooltip}>
        <span style={{ cursor: tooltip ? "help" : "default" }}>
          <SectionLabel>{label}</SectionLabel>
        </span>
      </Tooltip>
      <div
        style={{
          fontSize: 30,
          fontWeight: 600,
          lineHeight: 1.15,
          marginTop: space.hair,
          color: token.colorText,
          fontVariantNumeric: "proportional-nums",
          whiteSpace: "nowrap",
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: token.colorTextTertiary,
              marginLeft: space.hair + 2,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {footer}
    </div>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();

  const [preset, setPreset] = useState<Preset>("30d");
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await ApiService.apiMainAnalyticsDashboardRetrieve(
          range.from,
          range.to,
          templateId
        );
        if (!cancelled) setData(result);
      } catch (e) {
        // Never `catch { console.log(e) }` — a refusal the server wrote in
        // plain English is the most useful thing on the screen when it fails.
        if (!cancelled) setError(apiErrorMessage(e, t("Could not load analytics.")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, templateId, reload]);

  // Cases move while this tab sits in the background; a dashboard showing
  // yesterday's counts is the one thing worse than no dashboard.
  useRefreshOnTabFocus(useCallback(() => setReload((n) => n + 1), []));

  const kpis = data?.kpis;
  const singleProcess = templateId !== undefined;
  const kpiColumns = screens.xl ? 4 : screens.sm ? 2 : 1;

  /** Where a row drills down to: that process's list, at that state. */
  const listUrl = (template: AnalyticsTemplate, statusId?: number) =>
    template.type_short_name
      ? `/requests/${template.type_short_name}/${template.short_name}` +
        (statusId ? `?status=${statusId}` : "")
      : null;

  const openList = (template: AnalyticsTemplate, statusId?: number) => {
    const url = listUrl(template, statusId);
    if (url) navigate(url);
  };

  const comparison = t("Compared with {{range}}", {
    range: data
      ? readableRange(data.range.previous_from, data.range.previous_to)
      : "",
  });

  const activityTotal = (data?.activity ?? []).reduce(
    (sum, point) => sum + point.created + point.completed,
    0
  );
  // Only processes with something in them. A deployment carries templates
  // nobody has raised a case on for months (this one has three), and eight rows
  // of "0 started · 0 completed · 0 open" push the ones that matter off the
  // bottom of the panel while saying nothing. The count of the quiet ones is
  // still reported, because "we have processes nobody is using" is itself worth
  // knowing — it is just not worth six rows.
  const activeProcesses = (data?.by_template ?? []).filter(
    (row) => row.created + row.completed + row.open > 0
  );
  const quietProcesses = (data?.by_template ?? []).length - activeProcesses.length;
  // The bar, the number and the sort all measure the same thing. They did not:
  // the bar was drawn from started + open while the figure beside it showed only
  // what was open, so a row could carry a full-width bar next to a small number
  // and there was no way to reconcile the two by looking.
  const busiest = Math.max(1, ...activeProcesses.map((row) => row.created));
  const mostWaiting = Math.max(1, ...(data?.attention ?? []).map((row) => row.count));

  const controls = (
    <div
      style={{
        display: "flex",
        gap: space.tight,
        flexWrap: "wrap",
        justifyContent: "flex-end",
        alignItems: "center",
      }}
    >
      <Select
        value={templateId ?? "all"}
        style={{ minWidth: 200 }}
        onChange={(value) => setTemplateId(value === "all" ? undefined : Number(value))}
        showSearch
        optionFilterProp="label"
        options={[
          { value: "all", label: t("All processes") },
          ...(data?.templates ?? []).map((template) => ({
            value: template.id,
            label: template.name,
          })),
        ]}
      />
      <Segmented
        value={preset}
        onChange={(value) => setPreset(value as Preset)}
        options={[
          { label: t("7 days"), value: "7d" },
          { label: t("30 days"), value: "30d" },
          { label: t("90 days"), value: "90d" },
          { label: t("12 months"), value: "12m" },
          { label: t("Custom"), value: "custom" },
        ]}
      />
      {preset === "custom" && (
        <DatePicker.RangePicker
          value={custom}
          allowClear={false}
          onChange={(value) => value && setCustom(value as [Dayjs, Dayjs])}
        />
      )}
      <Tooltip title={t("Refresh")}>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => setReload((n) => n + 1)}
          loading={loading}
        />
      </Tooltip>
    </div>
  );

  return (
    <div style={{ maxWidth: MAX_WIDTH }}>
      <PageHeader
        title={t("Analytics")}
        meta={
          <>
            <span>{t("How your processes are performing")}</span>
            {data && (
              <>
                <MetaDot />
                <span>{readableRange(data.range.from, data.range.to)}</span>
              </>
            )}
          </>
        }
        actions={controls}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: space.base }}
          message={t("Analytics could not be loaded")}
          description={error}
          action={
            <Button size="small" onClick={() => setReload((n) => n + 1)}>
              {t("Try again")}
            </Button>
          }
        />
      )}

      {/* ------------------------------------------------ how much is happening */}
      <Panel padding={space.wide} style={{ marginBottom: space.base }}>
        {loading && !data ? (
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        ) : (
          // An explicit grid, not a wrapping flex row: the hairlines between
          // the figures are only correct if the code knows where the row
          // breaks, and `flex-wrap` decides that from widths at render time.
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${kpiColumns}, minmax(0, 1fr))`,
              gap: space.wide,
              maxWidth: KPI_BAND_MAX,
            }}
          >
            <Kpi
              divider={false}
              label={t("Started")}
              value={kpis?.created.value ?? 0}
              tooltip={t("Cases raised inside the selected period.")}
              footer={
                <Trend
                  t={t}
                  deltaPct={kpis?.created.delta_pct ?? null}
                  previous={kpis?.created.previous ?? null}
                  comparison={comparison}
                />
              }
            />
            <Kpi
              divider={1 % kpiColumns !== 0}
              label={t("Completed")}
              value={kpis?.completed.value ?? 0}
              tooltip={t("Cases that reached a final state in the period — finished or refused.")}
              footer={
                <Trend
                  t={t}
                  deltaPct={kpis?.completed.delta_pct ?? null}
                  previous={kpis?.completed.previous ?? null}
                  direction="up"
                  comparison={comparison}
                />
              }
            />
            <Kpi
              divider={2 % kpiColumns !== 0}
              label={t("In progress")}
              value={kpis?.in_progress.value ?? 0}
              tooltip={t("Open right now, whatever the selected period.")}
              footer={
                <div
                  style={{
                    marginTop: space.hair,
                    fontSize: 12,
                    color: kpis?.in_progress.overdue
                      ? token.colorText
                      : token.colorTextTertiary,
                  }}
                >
                  {kpis?.in_progress.overdue
                    ? t("{{count}} past their end date", {
                        count: kpis.in_progress.overdue,
                      })
                    : t("None past its end date")}
                </div>
              }
            />
            <Kpi
              divider={3 % kpiColumns !== 0}
              label={t("Average time to complete")}
              value={
                kpis?.avg_completion_hours.value === null ||
                kpis?.avg_completion_hours.value === undefined ? (
                  <span style={{ color: token.colorTextQuaternary }}>—</span>
                ) : (
                  formatDuration(kpis.avg_completion_hours.value, t)
                )
              }
              tooltip={t("From the case being raised to it reaching a final state.")}
              footer={
                kpis?.avg_completion_hours.sample ? (
                  <Trend
                    t={t}
                    deltaPct={kpis.avg_completion_hours.delta_pct}
                    previous={kpis.avg_completion_hours.previous}
                    direction="down"
                    comparison={`${comparison} · ${t(
                      "based on {{count}} completed cases",
                      { count: kpis.avg_completion_hours.sample }
                    )}`}
                  />
                ) : (
                  <div
                    style={{
                      marginTop: space.hair,
                      fontSize: 12,
                      color: token.colorTextTertiary,
                    }}
                  >
                    {t("Nothing completed yet in this period")}
                  </div>
                )
              }
            />
          </div>
        )}
      </Panel>

      {/* ------------------------------------------------- how it is progressing */}
      <Panel padding={space.wide} style={{ marginBottom: space.base }}>
        <PanelHeading
          title={t("Process activity")}
          hint={t("Cases started and completed over the selected period")}
          extra={
            activityTotal > 0 && (
              <div style={{ display: "flex", gap: space.base }}>
                <SeriesKey color={ACTIVITY_COLORS.started} label={t("Started")} />
                <SeriesKey color={ACTIVITY_COLORS.completed} label={t("Completed")} />
              </div>
            )
          }
        />
        {loading && !data ? (
          <Skeleton active paragraph={{ rows: 5 }} title={false} />
        ) : activityTotal === 0 ? (
          <EmptyState
            height={240}
            title={t("No activity in this period")}
            hint={t(
              "Once cases are raised and completed, this shows how the two keep pace — and whether work is piling up."
            )}
          />
        ) : (
          <ActivityChart activity={data!.activity} bucket={data!.range.bucket} />
        )}
      </Panel>

      {/* ----------------------------------------------------- where it is slow
          Its own band and its own call, after "how is it progressing" and
          before "where are the problems": the order of the page is the order
          people ask the questions, and "why is it slow" comes after seeing
          that it is. Its own fetch because it reads a wider default window --
          a step is only measured once a case leaves it. */}
      <ProcessSpeed
        dateFrom={range.from}
        dateTo={range.to}
        template={templateId}
        singleProcess={singleProcess}
      />

      {/* --------------------------------------------- where the problems are */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: screens.lg ? "1.4fr 1fr" : "1fr",
          gap: space.base,
          marginBottom: space.base,
        }}
      >
        <Panel padding={space.wide}>
          <PanelHeading
            title={t("Where work is waiting")}
            hint={t("Open cases by the state they are sitting in, right now")}
          />
          {loading && !data ? (
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          ) : (data?.attention ?? []).length === 0 ? (
            <EmptyState
              title={t("Nothing is waiting")}
              hint={t(
                "Every case in view has finished. States holding open cases appear here, most cases first."
              )}
            />
          ) : (
            <div>
              {data!.attention.map((row, index) => (
                <div key={row.status_id}>
                  {index > 0 && <Divider />}
                  <BarRow
                    share={row.count / mostWaiting}
                    color={ACTIVITY_COLORS.started}
                    title={
                      listUrl(row.template, row.status_id)
                        ? t("Open the list of these cases")
                        : undefined
                    }
                    onClick={
                      listUrl(row.template, row.status_id)
                        ? () => openList(row.template, row.status_id)
                        : undefined
                    }
                    label={
                      <>
                        {row.status_name}
                        {!singleProcess && (
                          <>
                            {" "}
                            <Chip>{row.template.short_name}</Chip>
                          </>
                        )}
                      </>
                    }
                    sublabel={t("Waiting {{average}} on average · longest {{longest}}", {
                      average: formatDays(row.avg_waiting_days, t),
                      longest: formatDays(row.longest_waiting_days, t),
                    })}
                    value={row.count}
                    meta={t("cases")}
                  />
                </div>
              ))}
              {data!.attention_truncated > 0 && (
                <div
                  style={{
                    marginTop: space.snug,
                    fontSize: 12,
                    color: token.colorTextTertiary,
                  }}
                >
                  {t("{{count}} more states hold fewer cases", {
                    count: data!.attention_truncated,
                  })}
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel padding={space.wide}>
          <PanelHeading
            title={t("Recently completed")}
            hint={t("The last cases to reach a final state")}
          />
          {loading && !data ? (
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          ) : (data?.recent ?? []).length === 0 ? (
            <EmptyState
              title={t("Nothing completed in this period")}
              hint={t(
                "Cases appear here as they finish, with how long each one took end to end."
              )}
            />
          ) : (
            <div>
              {data!.recent.map((row, index) => (
                <div key={row.id}>
                  {index > 0 && <Divider />}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/requests/${row.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/requests/${row.id}`);
                      }
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background = token.colorFillQuaternary;
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = "transparent";
                    }}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: space.snug,
                      padding: `${space.snug}px ${space.tight}px`,
                      margin: `0 -${space.tight}px`,
                      borderRadius: token.borderRadius,
                      cursor: "pointer",
                      transition: "background 120ms",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          color: token.colorText,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.number}
                        {!singleProcess && (
                          <>
                            {" "}
                            <Chip>{row.template.short_name}</Chip>
                          </>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: token.colorTextTertiary,
                          marginTop: 2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.title || row.status_name}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                      <div style={{ fontSize: 13, color: token.colorText }}>
                        {row.hours === null
                          ? "—"
                          : t("took {{duration}}", {
                              duration: formatDuration(row.hours, t),
                            })}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: token.colorTextTertiary,
                          marginTop: 2,
                        }}
                      >
                        {formatAgo(row.completed_at, t)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* ------------------------------------------------------------ by process */}
      {!singleProcess && (
        <Panel padding={space.wide}>
          <PanelHeading
            title={t("By process")}
            hint={t("Volume in the selected period, and what is open right now")}
          />
          {loading && !data ? (
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          ) : activeProcesses.length === 0 ? (
            <EmptyState
              title={t("No activity to break down")}
              hint={t(
                "Processes appear here once cases are raised on them, busiest first."
              )}
            />
          ) : (
            <div>
              {activeProcesses.map((row, index) => (
                <div key={row.id}>
                  {index > 0 && <Divider />}
                  <BarRow
                    share={row.created / busiest}
                    color={ACTIVITY_COLORS.started}
                    title={
                      listUrl(row) ? t("Open this process's case list") : undefined
                    }
                    onClick={listUrl(row) ? () => openList(row) : undefined}
                    label={
                      <>
                        {row.name} <Chip>{row.short_name}</Chip>
                      </>
                    }
                    sublabel={t("{{completed}} completed · {{open}} open now", {
                      completed: row.completed,
                      open: row.open,
                    })}
                    value={row.created}
                    meta={t("started")}
                  />
                </div>
              ))}
              {quietProcesses > 0 && (
                <div
                  style={{
                    marginTop: space.snug,
                    fontSize: 12,
                    color: token.colorTextTertiary,
                  }}
                >
                  {t("{{count}} more processes had nothing in this period", {
                    count: quietProcesses,
                  })}
                </div>
              )}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};

export default DashboardPage;
